/**
 * feedContext — single source of truth for all feed state.
 * Each top tab (Trending / WLB / Buddies / Groups) keeps an independent cache
 * so switching tabs restores posts instantly without a full-screen loader.
 */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  useMemo,
} from 'react';
import feedService from '../api/services/feedService';
import { getReactionById } from '../constants/reactions';
import { rankTrendingPosts } from '../utils/trendingRank';
import { useAuth } from './AuthContext';
import { eventBus } from '../platform/eventBus';
import { subscribeToAppForeground } from '../platform/appLifecycle';
import { ensureValidAccessToken } from '../api/auth/tokenRefresh';
import { USER_SESSION_CLEARED_EVENT } from '../api/auth/clearUserSession';
import { GROUP_MEMBERSHIP_CHANGED_EVENT } from './GroupContext';
import type {
  CreatePostPayload,
  FeedContextValue,
  FeedFilterParams,
  FeedPost,
  FeedState,
  FeedTab,
  Profile,
  TranslateFn,
} from '../types';
import { DEFAULT_FEED_FILTER_PARAMS } from '../types';
import { getUserFacingErrorMessage, UnusableResponseError } from '../utils/apiErrorHandler';
import { useTranslation } from '../i18n/useTranslation';

/** Snapshot of one top-tab's feed list + pagination/filters. */
interface FeedTabSnapshot {
  posts: FeedPost[];
  hasNextPage: boolean;
  page: number;
  activeFilters: FeedFilterParams | null;
  searchQuery: string | null;
  error: string | null;
  /** True once this tab has completed at least one successful load. */
  loaded: boolean;
  /** Timestamp when the snapshot was cached, for staleness checks. */
  cachedAt: number;
}

const initialState: FeedState = {
  activeTab: 'wlb',
  posts: [],
  loading: false,
  isRefreshing: false,
  isFetchingMore: false,
  hasNextPage: false,
  page: 1,
  error: null,
  loaded: false,
  loadMoreError: null,
  activeFilters: null,
  searchQuery: null,
};

const FEED_ACTIONS = {
  TAB_CHANGE: 'TAB_CHANGE',
  SET_FILTER: 'SET_FILTER',
  CLEAR_FILTER: 'CLEAR_FILTER',
  SET_SEARCH: 'SET_SEARCH',
  SET_FILTER_AND_SEARCH: 'SET_FILTER_AND_SEARCH',
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  REFRESH_START: 'REFRESH_START',
  REFRESH_SUCCESS: 'REFRESH_SUCCESS',
  REFRESH_ERROR: 'REFRESH_ERROR',
  LOAD_MORE_START: 'LOAD_MORE_START',
  LOAD_MORE_SUCCESS: 'LOAD_MORE_SUCCESS',
  LOAD_MORE_ERROR: 'LOAD_MORE_ERROR',
  LIKE_TOGGLE: 'LIKE_TOGGLE',
  SAVE_TOGGLE: 'SAVE_TOGGLE',
  POST_DELETE: 'POST_DELETE',
  POST_UPDATE: 'POST_UPDATE',
  UPDATE_COMMENT_COUNT: 'UPDATE_COMMENT_COUNT',
  UPDATE_SHARE_COUNT: 'UPDATE_SHARE_COUNT',
  PREPEND_NEW_POSTS: 'PREPEND_NEW_POSTS',
  BLOCK_USER_TOGGLE: 'BLOCK_USER_TOGGLE',
  SYNC_AUTHOR_PROFILE: 'SYNC_AUTHOR_PROFILE',
  RESET: 'RESET',
} as const;

type FeedAction =
  | {
      type: typeof FEED_ACTIONS.TAB_CHANGE;
      tab: FeedTab;
      /** Restored snapshot when revisiting a cached tab. */
      snapshot?: FeedTabSnapshot | null;
    }
  | { type: typeof FEED_ACTIONS.SET_FILTER; filters: FeedFilterParams }
  | { type: typeof FEED_ACTIONS.CLEAR_FILTER }
  | { type: typeof FEED_ACTIONS.SET_SEARCH; query: string | null }
  | { type: typeof FEED_ACTIONS.SET_FILTER_AND_SEARCH; filters: FeedFilterParams; query: string | null }
  | { type: typeof FEED_ACTIONS.FETCH_START }
  | {
      type: typeof FEED_ACTIONS.FETCH_SUCCESS;
      posts: FeedPost[];
      hasNextPage: boolean;
    }
  | { type: typeof FEED_ACTIONS.FETCH_ERROR; error: string }
  | { type: typeof FEED_ACTIONS.REFRESH_START }
  | {
      type: typeof FEED_ACTIONS.REFRESH_SUCCESS;
      posts: FeedPost[];
      hasNextPage: boolean;
    }
  | { type: typeof FEED_ACTIONS.REFRESH_ERROR; error: string }
  | { type: typeof FEED_ACTIONS.LOAD_MORE_START }
  | {
      type: typeof FEED_ACTIONS.LOAD_MORE_SUCCESS;
      posts: FeedPost[];
      hasNextPage: boolean;
    }
  | { type: typeof FEED_ACTIONS.LOAD_MORE_ERROR; error: string }
  | { type: typeof FEED_ACTIONS.LIKE_TOGGLE; postId: string; liked?: boolean; nextLikes?: number; reactionTypeId?: number | null; reactionName?: string | null }
  | { type: typeof FEED_ACTIONS.UPDATE_COMMENT_COUNT; postId: string; delta: number }
  | { type: typeof FEED_ACTIONS.UPDATE_SHARE_COUNT; postId: string }
  | { type: typeof FEED_ACTIONS.SAVE_TOGGLE; postId: string }
  | { type: typeof FEED_ACTIONS.POST_DELETE; postId: string }
  | {
      type: typeof FEED_ACTIONS.POST_UPDATE;
      postId: string;
      updates: Partial<FeedPost>;
    }
  | {
      type: typeof FEED_ACTIONS.PREPEND_NEW_POSTS;
      posts: FeedPost[];
    }
  | {
      type: typeof FEED_ACTIONS.BLOCK_USER_TOGGLE;
      userId?: string | number | null;
      username?: string;
      isBlocked?: boolean;
    }
  | {
      type: typeof FEED_ACTIONS.SYNC_AUTHOR_PROFILE;
      userId: string | number;
      username?: string;
      avatar?: FeedPost['avatar'];
    }
  | { type: typeof FEED_ACTIONS.RESET };

/** Keep first occurrence of each post id (string-normalized). */
const dedupePostsById = (posts: FeedPost[]): FeedPost[] => {
  const seen = new Set<string>();
  const result: FeedPost[] = [];
  for (const post of posts) {
    const id = String(post.id ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(post);
  }
  return result;
};

const feedReducer = (state: FeedState, action: FeedAction): FeedState => {
  switch (action.type) {
    case FEED_ACTIONS.TAB_CHANGE: {
      // Groups is a separate UI surface — never full-screen feed-load it.
      if (action.tab === 'groups') {
        return {
          ...state,
          activeTab: action.tab,
          loading: false,
          isRefreshing: false,
          isFetchingMore: false,
          error: null,
          // The Groups surface owns its own state; nothing here is pending.
          loaded: true,
          loadMoreError: null,
        };
      }

      const snap = action.snapshot;
      if (snap?.loaded) {
        // Instant restore — keep prior posts visible, no loader.
        return {
          ...state,
          activeTab: action.tab,
          posts: dedupePostsById(snap.posts),
          hasNextPage: snap.hasNextPage,
          page: snap.page,
          activeFilters: snap.activeFilters,
          searchQuery: snap.searchQuery,
          error: snap.error,
          loaded: true,
          loadMoreError: null,
          loading: false,
          isRefreshing: false,
          isFetchingMore: false,
        };
      }

      // First visit for this tab — show loader only because list is empty.
      return {
        ...state,
        activeTab: action.tab,
        posts: [],
        hasNextPage: false,
        page: 1,
        activeFilters: null,
        searchQuery: null,
        error: null,
        loaded: false,
        loadMoreError: null,
        loading: true,
        isRefreshing: false,
        isFetchingMore: false,
      };
    }

    case FEED_ACTIONS.SET_FILTER:
      return {
        ...state,
        activeFilters: action.filters,
        searchQuery: null,
        // Keep existing posts visible while filter request runs (no empty flash).
        loading: state.posts.length === 0,
        page: 1,
      };

    case FEED_ACTIONS.CLEAR_FILTER:
      return {
        ...state,
        activeFilters: null,
        searchQuery: null,
        loading: state.posts.length === 0,
        page: 1,
      };

    case FEED_ACTIONS.SET_SEARCH:
      return {
        ...state,
        activeFilters: null,
        searchQuery: action.query,
        loading: state.posts.length === 0,
        page: 1,
      };

    case FEED_ACTIONS.SET_FILTER_AND_SEARCH:
      return {
        ...state,
        activeFilters: action.filters,
        searchQuery: action.query,
        loading: state.posts.length === 0,
        page: 1,
      };

    case FEED_ACTIONS.FETCH_START:
      return {
        ...state,
        loading: state.posts.length === 0,
        error: null,
      };

    case FEED_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        posts: dedupePostsById(action.posts),
        hasNextPage: action.hasNextPage,
        page: 1,
        error: null,
        loaded: true,
        loadMoreError: null,
      };
    // `loaded` is left alone: a first load that failed must not let the screen
    // claim the feed is empty.
    case FEED_ACTIONS.FETCH_ERROR:
      return { ...state, loading: false, error: action.error };

    case FEED_ACTIONS.REFRESH_START:
      return { ...state, isRefreshing: true, error: null, loadMoreError: null };
    case FEED_ACTIONS.REFRESH_SUCCESS:
      return {
        ...state,
        isRefreshing: false,
        posts: dedupePostsById(action.posts),
        hasNextPage: action.hasNextPage,
        page: 1,
        error: null,
        loaded: true,
      };
    // Keeps the failure on screen: REFRESH_START cleared it, so without this a
    // retry that fails again would fall back to the empty state.
    case FEED_ACTIONS.REFRESH_ERROR:
      return { ...state, isRefreshing: false, error: action.error };

    case FEED_ACTIONS.LOAD_MORE_START:
      return { ...state, isFetchingMore: true, loadMoreError: null };
    case FEED_ACTIONS.LOAD_MORE_SUCCESS: {
      const existingIds = new Set(state.posts.map(p => String(p.id)));
      const appended = action.posts.filter(p => !existingIds.has(String(p.id)));
      return {
        ...state,
        isFetchingMore: false,
        posts: appended.length ? [...state.posts, ...appended] : state.posts,
        hasNextPage: action.hasNextPage,
        page: state.page + 1,
      };
    }
    case FEED_ACTIONS.LOAD_MORE_ERROR:
      return { ...state, isFetchingMore: false, loadMoreError: action.error };

    case FEED_ACTIONS.LIKE_TOGGLE:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (post.id !== action.postId) return post;
          const nextLiked = action.liked !== undefined ? action.liked : !post.liked;
          return {
            ...post,
            liked: nextLiked,
            likes:
              action.nextLikes !== undefined
                ? action.nextLikes
                : nextLiked
                ? post.likes + 1
                : Math.max(0, post.likes - 1),
            reactionTypeId:
              action.reactionTypeId !== undefined
                ? action.reactionTypeId
                : nextLiked
                ? (post.reactionTypeId ?? 1)
                : null,
            reactionName:
              action.reactionName !== undefined
                ? action.reactionName
                : nextLiked
                ? (post.reactionName ?? 'Like')
                : null,
          };
        }),
      };

    case FEED_ACTIONS.UPDATE_COMMENT_COUNT:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (post.id !== action.postId) return post;
          return {
            ...post,
            comments: Math.max(0, post.comments + action.delta),
          };
        }),
      };
    case FEED_ACTIONS.UPDATE_SHARE_COUNT:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (String(post.id) !== String(action.postId)) return post;
          const nextCount = (post.shares || (post as { totalShares?: number }).totalShares || 0) + 1;
          return {
            ...post,
            shares: nextCount,
            totalShares: nextCount,
          };
        }),
      };

    case FEED_ACTIONS.SAVE_TOGGLE:
      return {
        ...state,
        posts: state.posts.map(post =>
          post.id !== action.postId ? post : { ...post, saved: !post.saved },
        ),
      };

    case FEED_ACTIONS.BLOCK_USER_TOGGLE:
      return {
        ...state,
        posts: state.posts.map(post => {
          const matchUser =
            (action.userId && String(post.userId) === String(action.userId)) ||
            (action.username && post.username === action.username);
          if (!matchUser) return post;
          return {
            ...post,
            isBlocked:
              typeof action.isBlocked === 'boolean'
                ? action.isBlocked
                : !post.isBlocked,
          };
        }),
      };

    case FEED_ACTIONS.SYNC_AUTHOR_PROFILE:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (String(post.userId) !== String(action.userId)) return post;
          return {
            ...post,
            ...(action.username != null ? { username: action.username } : null),
            ...(action.avatar !== undefined ? { avatar: action.avatar } : null),
          };
        }),
      };

    case FEED_ACTIONS.POST_DELETE:
      return {
        ...state,
        posts: state.posts.filter(post => post.id !== action.postId),
      };

    case FEED_ACTIONS.POST_UPDATE:
      return {
        ...state,
        posts: state.posts.map(post =>
          post.id !== action.postId ? post : { ...post, ...action.updates },
        ),
      };
    case FEED_ACTIONS.PREPEND_NEW_POSTS: {
      const existingIds = new Set(state.posts.map(p => String(p.id)));
      const newPosts = action.posts.filter(p => !existingIds.has(String(p.id)));
      return {
        ...state,
        posts: newPosts.length ? [...newPosts, ...state.posts] : state.posts,
      };
    }
    case FEED_ACTIONS.RESET:
      return { ...initialState };
    default:
      return state;
  }
};

const feedContext = createContext<FeedContextValue | null>(null);

const snapshotFromState = (state: FeedState, loaded = true): FeedTabSnapshot => ({
  posts: state.posts,
  hasNextPage: state.hasNextPage,
  page: state.page,
  activeFilters: state.activeFilters,
  searchQuery: state.searchQuery,
  error: state.error,
  loaded,
  cachedAt: Date.now(),
});

export const FeedProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [state, dispatch] = useReducer(feedReducer, initialState);
  const { isAuthenticated } = useAuth();
  const { t: tFn } = useTranslation();
  const wasAuthenticatedRef = useRef(false);
  const activeFetchId = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  /** Independent cache per top tab — survives tab switches without remount. */
  const tabCacheRef = useRef<Record<string, FeedTabSnapshot>>({});

  // Temporary memory for hashtag filter (cleared on app kill)
  const [tempHashtagFilter, setTempHashtagFilter] = useState<string | null>(null);

  /** Coalesce post-menu silent GetFeeds refreshes into one in-flight request. */
  const feedRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedRefreshInFlightRef = useRef(false);
  const feedRefreshQueuedRef = useRef(false);

  /** Cross-screen share-count sync for screens that keep local post lists. */
  const [feedShareMutationVersion, setFeedShareMutationVersion] = useState(0);
  const [lastFeedShareMutation, setLastFeedShareMutation] = useState<{
    postId: string;
  } | null>(null);

  const mutateAllTabCaches = useCallback(
    (mutator: (posts: FeedPost[]) => FeedPost[]) => {
      Object.keys(tabCacheRef.current).forEach(tab => {
        const snap = tabCacheRef.current[tab as FeedTab];
        if (!snap) return;
        tabCacheRef.current[tab as FeedTab] = {
          ...snap,
          posts: mutator(snap.posts),
        };
      });
    },
    [],
  );

  const findPostAcrossCaches = useCallback((postId: string): FeedPost | undefined => {
    const fromActive = stateRef.current.posts.find(p => p.id === postId);
    if (fromActive) return fromActive;
    for (const snap of Object.values(tabCacheRef.current)) {
      const found = snap.posts.find(p => p.id === postId);
      if (found) return found;
    }
    return undefined;
  }, []);

  const persistActiveTab = useCallback(() => {
    const current = stateRef.current;
    if (current.activeTab === 'groups') return;
    // Don't overwrite a loaded cache with an in-flight empty loading state.
    if (current.loading && current.posts.length === 0) return;
    tabCacheRef.current[current.activeTab] = snapshotFromState(
      current,
      current.posts.length > 0 || !!tabCacheRef.current[current.activeTab]?.loaded,
    );
  }, []);

  const fetchInitial = useCallback(async (
    tab: FeedTab,
    filters?: FeedFilterParams | null,
    search?: string | null,
  ) => {
    if (tab === 'groups') return;
    const fetchId = ++activeFetchId.current;
    try {
      let data: FeedPost[] = [];
      let hasNextPage = false;

      if (filters) {
        const filterRes = await feedService.filterPosts(filters, 1);
        if (!filterRes.success) {
          console.warn('[FeedContext] filterPosts failed');
          throw new UnusableResponseError('FilterPosts');
        }
        data = filterRes.data || [];
        hasNextPage = filterRes.hasNextPage || false;
      } else {
        const res = await feedService.getFeed(tab, 1, search ?? undefined);
        data = res.data;
        hasNextPage = res.hasNextPage;
      }
      
      if (activeFetchId.current !== fetchId) return;
      // Ignore late responses if the user already left this tab.
      if (stateRef.current.activeTab !== tab) return;
      dispatch({ type: FEED_ACTIONS.FETCH_SUCCESS, posts: data, hasNextPage });
      tabCacheRef.current[tab] = {
        posts: dedupePostsById(data),
        hasNextPage,
        page: 1,
        activeFilters: filters ?? null,
        searchQuery: search ?? null,
        error: null,
        loaded: true,
        cachedAt: Date.now(),
      };
    } catch (err) {
      if (activeFetchId.current !== fetchId) return;
      if (stateRef.current.activeTab !== tab) return;
      dispatch({
        type: FEED_ACTIONS.FETCH_ERROR,
        error: getUserFacingErrorMessage(err, tFn('common.errors.generic')),
      });
    }
  }, [tFn]);

  const setActiveTab = useCallback(
    (tab: FeedTab) => {
      const current = stateRef.current;
      if (tab === current.activeTab) return;

      // Save outgoing tab before switching.
      persistActiveTab();

      const cached = tabCacheRef.current[tab] ?? null;
      dispatch({
        type: FEED_ACTIONS.TAB_CHANGE,
        tab,
        snapshot: cached,
      });

      // Groups has its own data layer — never call GetFeeds.
      if (tab === 'groups') return;

      // Revisit with cache — no network.
      if (cached?.loaded) return;

      // First visit only.
      fetchInitial(tab, null, null);
    },
    [fetchInitial, persistActiveTab],
  );

  const applyFilter = useCallback(
    async (filters: FeedFilterParams) => {
      dispatch({ type: FEED_ACTIONS.SET_FILTER, filters });
      await fetchInitial(stateRef.current.activeTab, filters, null);
    },
    [fetchInitial],
  );

  const applyFilterAndSearch = useCallback(
    async (filters: FeedFilterParams, searchString: string | null) => {
      dispatch({ type: FEED_ACTIONS.SET_FILTER_AND_SEARCH, filters, query: searchString });

      const fetchId = ++activeFetchId.current;
      dispatch({ type: FEED_ACTIONS.FETCH_START });

      const tab = stateRef.current.activeTab;

      try {
        let posts: FeedPost[];
        let hasNextPage: boolean;

        if (searchString) {
          // Search-only path: getFeed supports a search param.
          // Filter params are not supported by getFeed so they are persisted
          // in state only (for display) but not applied server-side here.
          const res = await feedService.getFeed(tab, 1, searchString);
          posts = res.data;
          hasNextPage = res.hasNextPage;
        } else {
          // Filter-only path: call FilterPost API first
          const filterRes = await feedService.filterPosts(filters, 1);
          if (!filterRes.success) {
            throw new UnusableResponseError('FilterPosts');
          }
          
          // filterPosts only updates the user preference on the server and returns empty data,
          // but we check for filterRes.data to satisfy review comments.
          if (filterRes.data && filterRes.data.length > 0) {
            posts = filterRes.data;
            hasNextPage = filterRes.hasNextPage ?? false;
            // Background update
            feedService.getFeed('wlb', 1).catch(() => undefined);
          } else {
            // Fetch the actual feeds using getFeed
            const feedRes = await feedService.getFeed('wlb', 1);
            posts = feedRes.data;
            hasNextPage = feedRes.hasNextPage;
          }
        }

        if (activeFetchId.current !== fetchId) return;
        if (stateRef.current.activeTab !== tab) return;

        dispatch({ type: FEED_ACTIONS.FETCH_SUCCESS, posts, hasNextPage });
        tabCacheRef.current[tab] = {
          posts: dedupePostsById(posts),
          hasNextPage,
          page: 1,
          activeFilters: filters,
          searchQuery: searchString,
          error: null,
          loaded: true,
          cachedAt: Date.now(),
        };
      } catch (err) {
        if (activeFetchId.current !== fetchId) return;
        if (stateRef.current.activeTab !== tab) return;
        dispatch({
          type: FEED_ACTIONS.FETCH_ERROR,
          error: getUserFacingErrorMessage(err, tFn('common.errors.generic')),
        });
        throw err;
      }
    },
    [tFn],
  );

  const clearFilter = useCallback(async () => {
    dispatch({ type: FEED_ACTIONS.CLEAR_FILTER });
    const tab = stateRef.current.activeTab;
    if (tab === 'groups') return;

    const fetchId = ++activeFetchId.current;
    dispatch({ type: FEED_ACTIONS.FETCH_START });
    
    try {
      const filterRes = await feedService.filterPosts(DEFAULT_FEED_FILTER_PARAMS, 1);
      if (!filterRes.success) {
        console.warn('[FeedContext] clearFilter filterPosts failed');
        throw new UnusableResponseError('FilterPosts');
      }
      
      const search = stateRef.current.searchQuery;
      const feedRes = await feedService.getFeed(tab, 1, search ?? undefined);

      const data = feedRes.data || [];
      const hasNextPage = feedRes.hasNextPage || false;
      
      if (activeFetchId.current !== fetchId) return;
      if (stateRef.current.activeTab !== tab) return;
      
      dispatch({ type: FEED_ACTIONS.FETCH_SUCCESS, posts: data, hasNextPage });
      tabCacheRef.current[tab] = {
        posts: dedupePostsById(data),
        hasNextPage,
        page: 1,
        activeFilters: null,
        searchQuery: stateRef.current.searchQuery,
        error: null,
        loaded: true,
        cachedAt: Date.now(),
      };
    } catch (err) {
      if (activeFetchId.current !== fetchId) return;
      if (stateRef.current.activeTab !== tab) return;
      dispatch({
        type: FEED_ACTIONS.FETCH_ERROR,
        error: getUserFacingErrorMessage(err, tFn('common.errors.generic')),
      });
    }
  }, [tFn]);

  /**
   * Search the active feed by hashtag(s).
   *
   * `query` is forwarded verbatim to the GetFeeds `search` field.
   * The backend accepts either a single hashtag (`"#fitness"`) or a
   * comma-separated list (`"#fitness,#weightloss,#healthy"`). Pass `null`
   * to clear the filter and reload the unfiltered feed.
   */
  const searchHashtag = useCallback(
    async (query: string | null) => {
      dispatch({ type: FEED_ACTIONS.SET_SEARCH, query });
      await fetchInitial(stateRef.current.activeTab, null, query);
    },
    [fetchInitial],
  );

  const refreshFeed = useCallback(async () => {
    const current = stateRef.current;
    if (current.activeTab === 'groups') return;
    dispatch({ type: FEED_ACTIONS.REFRESH_START });
    try {
      let data: FeedPost[];
      let hasNextPage: boolean;

      if (current.activeFilters && Object.keys(current.activeFilters).length > 0 && !current.searchQuery) {
        const filterRes = await feedService.filterPosts(current.activeFilters, 1);
        if (!filterRes.success) throw new UnusableResponseError('FilterPosts');
        
        if (filterRes.data && filterRes.data.length > 0) {
          data = filterRes.data;
          hasNextPage = filterRes.hasNextPage ?? false;
          feedService.getFeed(current.activeTab, 1).catch(() => undefined);
        } else {
          const res = await feedService.getFeed(current.activeTab, 1);
          data = res.data;
          hasNextPage = res.hasNextPage;
        }
      } else {
        const res = await feedService.getFeed(
          current.activeTab,
          1,
          current.searchQuery ?? undefined,
        );
        data = res.data;
        hasNextPage = res.hasNextPage;
      }
      const rankedData = current.activeTab === 'trending' ? rankTrendingPosts(data) : data;
      
      dispatch({
        type: FEED_ACTIONS.REFRESH_SUCCESS,
        posts: rankedData,
        hasNextPage,
      });
      tabCacheRef.current[current.activeTab] = {
        posts: dedupePostsById(rankedData),
        hasNextPage,
        page: 1,
        activeFilters: current.activeFilters,
        searchQuery: current.searchQuery,
        error: null,
        loaded: true,
        cachedAt: Date.now(),
      };
    } catch (err) {
      dispatch({
        type: FEED_ACTIONS.REFRESH_ERROR,
        error: getUserFacingErrorMessage(err, tFn('common.errors.generic')),
      });
    }
  }, [tFn]);

  /**
   * Retry after a failed load. Unlike `refreshFeed` this runs the initial fetch,
   * so the member gets the loading state back instead of a silent spinner on an
   * error block.
   */
  const retryFeed = useCallback(async () => {
    const current = stateRef.current;
    if (current.activeTab === 'groups') return;
    dispatch({ type: FEED_ACTIONS.FETCH_START });
    await fetchInitial(current.activeTab, current.activeFilters, current.searchQuery);
  }, [fetchInitial]);

  /**
   * Silent GetFeeds for the active tab (keeps filters/search; no pull-to-refresh UI).
   * Marks other tab caches stale so the next visit refetches.
   */
  const runSilentFeedRefresh = useCallback(async () => {
    const current = stateRef.current;
    if (current.activeTab === 'groups') return;

    feedRefreshInFlightRef.current = true;
    try {
      let data: FeedPost[];
      let hasNextPage: boolean;

      if (current.activeFilters && Object.keys(current.activeFilters).length > 0 && !current.searchQuery) {
        const filterRes = await feedService.filterPosts(current.activeFilters, 1);
        if (!filterRes.success) throw new UnusableResponseError('FilterPosts');
        
        if (filterRes.data && filterRes.data.length > 0) {
          data = filterRes.data;
          hasNextPage = filterRes.hasNextPage ?? false;
          feedService.getFeed(current.activeTab, 1).catch(() => undefined);
        } else {
          const res = await feedService.getFeed(current.activeTab, 1);
          data = res.data;
          hasNextPage = res.hasNextPage;
        }
      } else {
        const res = await feedService.getFeed(
          current.activeTab,
          1,
          current.searchQuery ?? undefined,
        );
        data = res.data;
        hasNextPage = res.hasNextPage;
      }

      const deduped = dedupePostsById(data);
      // Re-rank trending posts on silent refresh.
      const rankedDeduped = current.activeTab === 'trending' ? rankTrendingPosts(deduped) : deduped;
      dispatch({
        type: FEED_ACTIONS.REFRESH_SUCCESS,
        posts: rankedDeduped,
        hasNextPage,
      });
      tabCacheRef.current[current.activeTab] = {
        posts: rankedDeduped,
        hasNextPage,
        page: 1,
        activeFilters: current.activeFilters,
        searchQuery: current.searchQuery,
        error: null,
        loaded: true,
        cachedAt: Date.now(),
      };

      Object.keys(tabCacheRef.current).forEach(tab => {
        if (tab === current.activeTab) return;
        const snap = tabCacheRef.current[tab as FeedTab];
        if (snap) {
          tabCacheRef.current[tab as FeedTab] = { ...snap, loaded: false };
        }
      });
    } catch {
      // Soft refresh failures are non-blocking; pull-to-refresh can recover.
    } finally {
      feedRefreshInFlightRef.current = false;
      if (feedRefreshQueuedRef.current) {
        feedRefreshQueuedRef.current = false;
        runSilentFeedRefresh().catch(() => undefined);
      }
    }
  }, []);

  /** Debounced entry — one GetFeeds per burst of successful post-menu actions. */
  const requestFeedRefresh = useCallback(() => {
    if (stateRef.current.activeTab === 'groups') return;

    if (feedRefreshTimerRef.current) {
      clearTimeout(feedRefreshTimerRef.current);
    }

    feedRefreshTimerRef.current = setTimeout(() => {
      feedRefreshTimerRef.current = null;
      if (feedRefreshInFlightRef.current) {
        feedRefreshQueuedRef.current = true;
        return;
      }
      runSilentFeedRefresh().catch(() => undefined);
    }, 350);
  }, [runSilentFeedRefresh]);

  useEffect(() => {
    return () => {
      if (feedRefreshTimerRef.current) {
        clearTimeout(feedRefreshTimerRef.current);
      }
    };
  }, []);

  const fetchNextPage = useCallback(async () => {
    const current = stateRef.current;
    if (current.activeTab === 'groups') return;
    if (!current.hasNextPage || current.isFetchingMore) return;
    dispatch({ type: FEED_ACTIONS.LOAD_MORE_START });
    try {
      const nextPage = current.page + 1;

      let data: FeedPost[];
      let hasNextPageResult: boolean;

      if (current.activeFilters && Object.keys(current.activeFilters).length > 0 && !current.searchQuery) {
        const filterRes = await feedService.filterPosts(current.activeFilters, nextPage);
        if (!filterRes.success) throw new UnusableResponseError('FilterPosts');
        
        if (filterRes.data && filterRes.data.length > 0) {
          data = filterRes.data;
          hasNextPageResult = filterRes.hasNextPage ?? false;
          feedService.getFeed(current.activeTab, nextPage).catch(() => undefined);
        } else {
          const res = await feedService.getFeed(current.activeTab, nextPage);
          data = res.data;
          hasNextPageResult = res.hasNextPage;
        }
      } else {
        const res = await feedService.getFeed(
          current.activeTab,
          nextPage,
          current.searchQuery ?? undefined,
        );
        data = res.data;
        hasNextPageResult = res.hasNextPage;
      }
      dispatch({
        type: FEED_ACTIONS.LOAD_MORE_SUCCESS,
        posts: data,
        hasNextPage: hasNextPageResult,
      });
      const existingIds = new Set(current.posts.map(p => String(p.id)));
      const appended = data.filter(p => !existingIds.has(String(p.id)));
      tabCacheRef.current[current.activeTab] = {
        posts: appended.length ? [...current.posts, ...appended] : current.posts,
        hasNextPage: hasNextPageResult,
        page: nextPage,
        activeFilters: current.activeFilters,
        searchQuery: current.searchQuery,
        error: null,
        loaded: true,
        cachedAt: Date.now(),
      };
    } catch (err) {
      dispatch({
        type: FEED_ACTIONS.LOAD_MORE_ERROR,
        error: getUserFacingErrorMessage(err, tFn('common.errors.generic')),
      });
    }
  }, [tFn]);

  const likePost = useCallback(async (postId: string, reactionTypeId?: number) => {
    const post = findPostAcrossCaches(postId);
    const currentlyLiked = post?.liked ?? false;
    const currentReactionTypeId = post?.reactionTypeId ?? (currentlyLiked ? 1 : null);
    const currentReactionName = post?.reactionName ?? (currentlyLiked ? 'Like' : null);
    const currentLikes = post?.likes ?? 0;

    let nextLiked: boolean;
    let nextLikes: number;
    let nextReactionTypeId: number | null;
    let nextReactionName: string | null;

    if (reactionTypeId != null && reactionTypeId > 0) {
      const def = getReactionById(reactionTypeId);
      if (currentlyLiked && currentReactionTypeId === reactionTypeId) {
        // Toggle off (unlike)
        nextLiked = false;
        nextLikes = Math.max(0, currentLikes - 1);
        nextReactionTypeId = null;
        nextReactionName = null;
      } else if (currentlyLiked) {
        // Switch reaction (LinkedIn style - like count unchanged)
        nextLiked = true;
        nextLikes = currentLikes;
        nextReactionTypeId = reactionTypeId;
        nextReactionName = def?.name ?? 'Like';
      } else {
        // New reaction
        nextLiked = true;
        nextLikes = currentLikes + 1;
        nextReactionTypeId = reactionTypeId;
        nextReactionName = def?.name ?? 'Like';
      }
    } else {
      // Quick tap without reaction: toggle like / unlike
      nextLiked = !currentlyLiked;
      nextLikes = nextLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1);
      nextReactionTypeId = nextLiked ? 1 : null;
      nextReactionName = nextLiked ? 'Like' : null;
    }

    dispatch({
      type: FEED_ACTIONS.LIKE_TOGGLE,
      postId,
      liked: nextLiked,
      nextLikes,
      reactionTypeId: nextReactionTypeId,
      reactionName: nextReactionName,
    });

    mutateAllTabCaches(posts =>
      posts.map(p => {
        if (p.id !== postId) return p;
        return {
          ...p,
          liked: nextLiked,
          likes: nextLikes,
          reactionTypeId: nextReactionTypeId,
          reactionName: nextReactionName,
        };
      }),
    );

    try {
      const targetReactionTypeId = nextLiked
        ? (nextReactionTypeId ?? 1)
        : (currentReactionTypeId ?? reactionTypeId ?? 1);

      const res = await feedService.likePost(postId, {
        currentlyLiked,
        reactionTypeId: targetReactionTypeId,
      });
      if (!res.success) {
        // Rollback
        dispatch({
          type: FEED_ACTIONS.LIKE_TOGGLE,
          postId,
          liked: currentlyLiked,
          nextLikes: currentLikes,
          reactionTypeId: currentReactionTypeId,
          reactionName: currentReactionName,
        });
        mutateAllTabCaches(posts =>
          posts.map(p => {
            if (p.id !== postId) return p;
            return {
              ...p,
              liked: currentlyLiked,
              likes: currentLikes,
              reactionTypeId: currentReactionTypeId,
              reactionName: currentReactionName,
            };
          }),
        );
      }
    } catch {
      // Rollback on catch
      dispatch({
        type: FEED_ACTIONS.LIKE_TOGGLE,
        postId,
        liked: currentlyLiked,
        nextLikes: currentLikes,
        reactionTypeId: currentReactionTypeId,
        reactionName: currentReactionName,
      });
      mutateAllTabCaches(posts =>
        posts.map(p => {
          if (p.id !== postId) return p;
          return {
            ...p,
            liked: currentlyLiked,
            likes: currentLikes,
            reactionTypeId: currentReactionTypeId,
            reactionName: currentReactionName,
          };
        }),
      );
    }
  }, [findPostAcrossCaches, mutateAllTabCaches]);

  const updateCommentCount = useCallback(async (postId: string, delta: number) => {
    dispatch({ type: FEED_ACTIONS.UPDATE_COMMENT_COUNT, postId, delta });
    mutateAllTabCaches(posts =>
      posts.map(post =>
        post.id !== postId
          ? post
          : { ...post, comments: Math.max(0, post.comments + delta) },
      ),
    );
  }, [mutateAllTabCaches]);

  const savePost = useCallback(async (postId: string): Promise<boolean> => {
    const post = findPostAcrossCaches(postId);
    const targetIsSave = post ? !post.saved : true;
    dispatch({ type: FEED_ACTIONS.SAVE_TOGGLE, postId });
    mutateAllTabCaches(posts =>
      posts.map(p => (p.id !== postId ? p : { ...p, saved: !p.saved })),
    );
    try {
      const res = await feedService.savePost(postId, targetIsSave);
      if (!res.success) {
        dispatch({ type: FEED_ACTIONS.SAVE_TOGGLE, postId });
        mutateAllTabCaches(posts =>
          posts.map(p => (p.id !== postId ? p : { ...p, saved: !p.saved })),
        );
        return false;
      }
      return true;
    } catch {
      dispatch({ type: FEED_ACTIONS.SAVE_TOGGLE, postId });
      mutateAllTabCaches(posts =>
        posts.map(p => (p.id !== postId ? p : { ...p, saved: !p.saved })),
      );
      return false;
    }
  }, [findPostAcrossCaches, mutateAllTabCaches]);

  const sharePost = useCallback(async (
    postId: string,
    bodyText?: string,
    groupId?: string,
    privacyId?: number,
  ) => {
    const normalizedPostId = String(postId);
    const response = await feedService.sharePost(
      normalizedPostId,
      bodyText,
      groupId,
      privacyId,
    );
    if (response.success) {
      dispatch({ type: FEED_ACTIONS.UPDATE_SHARE_COUNT, postId: normalizedPostId });
      mutateAllTabCaches(posts =>
        posts.map(post => {
          if (String(post.id) !== normalizedPostId) return post;
          const nextCount = (post.shares || 0) + 1;
          return { ...post, shares: nextCount, totalShares: nextCount };
        }),
      );
      setLastFeedShareMutation({ postId: normalizedPostId });
      setFeedShareMutationVersion(v => v + 1);
    }
    return response;
  }, [mutateAllTabCaches]);

  const deletePost = useCallback(
    async (postId: string): Promise<boolean> => {
      dispatch({ type: FEED_ACTIONS.POST_DELETE, postId });
      mutateAllTabCaches(posts => posts.filter(post => post.id !== postId));
      try {
        const result = await feedService.deletePost(postId);
        return !!result?.success;
      } catch {
        // Keep optimistic removal; pull-to-refresh / soft refresh can resync.
        return false;
      }
    },
    [mutateAllTabCaches],
  );

  const updatePost = useCallback(
    async (
      postId: string,
      payload: CreatePostPayload,
      profile: Profile | null,
      t: TranslateFn,
    ) => {
      const response = await feedService.updatePost(
        postId,
        payload,
        profile,
        t,
      );
      if (response?.success && response.data?.post) {
        const existing = findPostAcrossCaches(postId);
        const optimistic = response.data.post;
        // Preserve engagement + identity fields the optimistic builder resets.
        const updates: Partial<FeedPost> = existing
          ? {
              text: optimistic.text,
              content: optimistic.content,
              feeling: optimistic.feeling,
              hashtags: optimistic.hashtags,
              taggedBuddies: optimistic.taggedBuddies,
              privacy: optimistic.privacy,
              image: optimistic.image,
              images: optimistic.images,
              imageLayout: optimistic.imageLayout,
              video: optimistic.video,
              groupId: optimistic.groupId ?? existing.groupId,
              groupName: optimistic.groupName ?? existing.groupName,
              // An edit re-runs moderation, so the verdict can flip either way
              // and must overwrite the cached one rather than be preserved.
              moderationStatus: optimistic.moderationStatus,
            }
          : optimistic;

        dispatch({
          type: FEED_ACTIONS.POST_UPDATE,
          postId,
          updates,
        });
        mutateAllTabCaches(posts =>
          posts.map(post =>
            post.id !== postId ? post : { ...post, ...updates },
          ),
        );
      }
      return response;
    },
    [findPostAcrossCaches, mutateAllTabCaches],
  );

  const syncNewPosts = useCallback((posts: FeedPost[]) => {
    if (!posts.length) {
      return;
    }
    // Only the active tab list — other tabs keep their existing cache until refresh.
    dispatch({ type: FEED_ACTIONS.PREPEND_NEW_POSTS, posts });
  }, []);

  const toggleBlockUser = useCallback((userId?: string | number | null, username?: string, isBlocked?: boolean) => {
    dispatch({ type: FEED_ACTIONS.BLOCK_USER_TOGGLE, userId, username, isBlocked });
    mutateAllTabCaches(posts =>
      posts.map(post => {
        const matchUser =
          (userId && String(post.userId) === String(userId)) ||
          (username && post.username === username);
        if (!matchUser) return post;
        return {
          ...post,
          isBlocked:
            typeof isBlocked === 'boolean' ? isBlocked : !post.isBlocked,
        };
      }),
    );
  }, [mutateAllTabCaches]);

  /** Keep own posts' author name/avatar in sync after profile create/update. */
  const syncAuthorProfile = useCallback(
    (
      userId: string | number | null | undefined,
      patch: { username?: string; avatar?: FeedPost['avatar'] },
    ) => {
      if (userId == null) return;
      dispatch({
        type: FEED_ACTIONS.SYNC_AUTHOR_PROFILE,
        userId,
        username: patch.username,
        avatar: patch.avatar,
      });
      mutateAllTabCaches(posts =>
        posts.map(post =>
          String(post.userId) === String(userId)
            ? {
                ...post,
                ...(patch.username != null ? { username: patch.username } : null),
                ...(patch.avatar !== undefined ? { avatar: patch.avatar } : null),
              }
            : post,
        ),
      );
    },
    [mutateAllTabCaches],
  );

  // Auto-recover stale feeds or error states when returning from the background
  useEffect(() => {
    return subscribeToAppForeground(() => {
      ensureValidAccessToken()
        .catch(() => undefined)
        .finally(() => {
          const current = stateRef.current;
          if (current.activeTab === 'groups') return;

          const cache = tabCacheRef.current[current.activeTab];
          const isError = current.error != null;
          // 15 minutes in milliseconds
          const isStale = !!cache?.cachedAt && (Date.now() - cache.cachedAt > 15 * 60 * 1000);

          // Do not trigger another fetch if one is already actively running
          if (current.loading || current.isRefreshing || feedRefreshInFlightRef.current) {
            return;
          }

          if (isError || isStale) {
            if (current.posts.length === 0 || isError) {
              fetchInitial(current.activeTab, current.activeFilters, current.searchQuery).catch(() => undefined);
            } else {
              runSilentFeedRefresh().catch(() => undefined);
            }
          }
        });
    });
  }, [fetchInitial, runSilentFeedRefresh]);

  // Keep the active tab's cache in sync with local mutations (like/save/delete/etc.).
  useEffect(() => {
    persistActiveTab();
  }, [
    state.activeTab,
    state.posts,
    state.hasNextPage,
    state.page,
    state.activeFilters,
    state.searchQuery,
    state.error,
    state.loading,
    persistActiveTab,
  ]);

  useEffect(() => {
    if (isAuthenticated && !wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = true;
      // First session load for the default tab — do not wipe via TAB_CHANGE.
      dispatch({ type: FEED_ACTIONS.FETCH_START });
      fetchInitial(stateRef.current.activeTab);
    } else if (!isAuthenticated) {
      wasAuthenticatedRef.current = false;
      tabCacheRef.current = {};
      dispatch({ type: FEED_ACTIONS.RESET });
    }
  }, [isAuthenticated, fetchInitial]);

  useEffect(() => {
    const sub = eventBus.addListener(USER_SESSION_CLEARED_EVENT, () => {
      wasAuthenticatedRef.current = false;
      tabCacheRef.current = {};
      dispatch({ type: FEED_ACTIONS.RESET });
    });
    const groupSub = eventBus.addListener(GROUP_MEMBERSHIP_CHANGED_EVENT, () => {
      refreshFeed().catch(() => {});
    });
    return () => {
      sub.remove();
      groupSub.remove();
    };
  }, [refreshFeed]);

  return (
    <feedContext.Provider
      value={useMemo(() => ({
        ...state,
        setActiveTab,
        refreshFeed,
        retryFeed,
        requestFeedRefresh,
        fetchNextPage,
        applyFilter,
        applyFilterAndSearch,
        clearFilter,
        searchHashtag,
        likePost,
        savePost,
        sharePost,
        deletePost,
        updatePost,
        updateCommentCount,
        syncNewPosts,
        toggleBlockUser,
        syncAuthorProfile,
        getCachedPost: findPostAcrossCaches,
        feedShareMutationVersion,
        lastFeedShareMutation,
        setTempHashtagFilter,
        tempHashtagFilter,
      }), [
        state,
        setActiveTab,
        refreshFeed,
        retryFeed,
        requestFeedRefresh,
        fetchNextPage,
        applyFilter,
        applyFilterAndSearch,
        clearFilter,
        searchHashtag,
        likePost,
        savePost,
        sharePost,
        deletePost,
        updatePost,
        updateCommentCount,
        syncNewPosts,
        toggleBlockUser,
        syncAuthorProfile,
        findPostAcrossCaches,
        feedShareMutationVersion,
        lastFeedShareMutation,
        tempHashtagFilter,
      ])}>
      {children}
    </feedContext.Provider>
  );
};

export const useFeed = (): FeedContextValue => {
  const feed = useContext(feedContext);
  if (!feed) {
    throw new Error('useFeed must be used within a <FeedProvider>');
  }
  return feed;
};

export default feedContext;
